import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { heroData, contactInfo } from "../data/mock";
import { ChevronRight, Phone } from "lucide-react";
import { scrollToSection } from "../lib/scroll";

const EASE = [0.16, 1, 0.3, 1];

const MaskedLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block will-change-transform"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

export const HeroSection = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 150, damping: 20 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 150, damping: 20 });

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  const handleMouseLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <section
      id="accueil"
      data-testid="hero-section"
      className="relative min-h-[100vh] flex items-center overflow-hidden bg-[#1a1d2e]"
    >
      {/* Ambient gold glows */}
      <div
        className="absolute -top-[25%] -right-[15%] w-[65vw] h-[65vw] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(201,168,76,0.09) 0%, transparent 60%)" }}
      />
      <div
        className="absolute bottom-[-35%] left-[-20%] w-[55vw] h-[55vw] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(201,168,76,0.05) 0%, transparent 60%)" }}
      />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 w-full pt-[150px] pb-[110px]">
        <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-16 lg:gap-12 items-center">
          {/* Left — kinetic copy */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
              className="inline-flex items-center gap-3 px-5 py-2.5 border border-[#c9a84c]/40 mb-8"
            >
              <span className="w-[6px] h-[6px] bg-[#c9a84c] rounded-full" />
              <span className="text-[12px] tracking-[0.15em] uppercase text-[#c9a84c]">
                Plus de 7 ans d'expérience
              </span>
            </motion.div>

            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-medium text-white leading-[1.1] tracking-tight mb-7"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            >
              <MaskedLine delay={0.25}>Votre chauffeur privé</MaskedLine>
              <MaskedLine delay={0.38}>
                dans le <em className="italic text-[#c9a84c]">Finistère</em>
              </MaskedLine>
            </h1>

            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.9, ease: EASE, delay: 0.6 }}
              className="gold-line origin-left mb-6"
            />

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE, delay: 0.65 }}
              className="text-[20px] md:text-[24px] font-normal text-[#c9a84c] mb-4 leading-[1.4]"
            >
              {heroData.subtitle}
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE, delay: 0.75 }}
              className="text-[16px] text-white/60 leading-[1.7] mb-10 max-w-[520px]"
            >
              {heroData.description}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE, delay: 0.85 }}
              className="flex flex-col sm:flex-row items-start gap-4"
            >
              <motion.a
                data-testid="hero-cta-reserve"
                href={contactInfo.phoneLink}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className="inline-flex items-center justify-center gap-2 px-8 py-5 bg-[#c9a84c] text-[#1a1d2e] text-[14px] font-medium tracking-[0.02em] hover:bg-[#d4af5c] transition-colors duration-200 min-w-[210px]"
              >
                <Phone size={16} strokeWidth={1.5} />
                {heroData.cta}
              </motion.a>
              <button
                data-testid="hero-cta-services"
                onClick={() => scrollToSection("#services")}
                className="inline-flex items-center gap-2 text-white/70 text-[14px] font-normal py-4 px-2 hover:text-[#c9a84c] transition-colors duration-200 group"
              >
                {heroData.ctaSecondary}
                <ChevronRight
                  size={16}
                  className="transition-transform duration-200 group-hover:translate-x-1"
                />
              </button>
            </motion.div>
          </div>

          {/* Right — 3D tilt photo card */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: EASE, delay: 0.5 }}
            className="relative"
            style={{ perspective: 1200 }}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          >
            <motion.div
              style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
              className="relative"
            >
              {/* Offset gold frame */}
              <div className="absolute -top-4 -right-4 w-full h-full border border-[#c9a84c]/40 pointer-events-none" />

              {/* Clipped photo */}
              <div
                className="relative overflow-hidden"
                style={{
                  clipPath:
                    "polygon(0 0, 100% 0, 100% calc(100% - 44px), calc(100% - 44px) 100%, 0 100%)",
                }}
              >
                <motion.img
                  src={heroData.image}
                  alt="Van Mercedes Classe V - Van d'Iroise, chauffeur privé Brest"
                  className="w-full h-[320px] md:h-[440px] lg:h-[520px] object-cover"
                  initial={{ scale: 1.15 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 1.8, ease: EASE, delay: 0.5 }}
                />
                {/* Spotlight treatment */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(ellipse at 30% 15%, rgba(255,255,255,0.12) 0%, transparent 55%), linear-gradient(to top, rgba(26,29,46,0.45) 0%, transparent 45%)",
                  }}
                />
              </div>

              {/* Floating rating badge */}
              <motion.div
                data-testid="hero-rating-badge"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: EASE, delay: 1.1 }}
                className="absolute -bottom-7 left-4 md:-left-6 bg-[#22263a] border border-[#c9a84c]/30 px-5 py-4 shadow-[0_20px_50px_rgba(0,0,0,0.45)]"
              >
                <div className="flex items-center gap-1 mb-1.5">
                  {[...Array(5)].map((_, i) => (
                    <svg
                      key={i}
                      className="w-[14px] h-[14px] text-[#c9a84c]"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-[13px] text-white/70">
                  <span className="text-white font-medium">4,9/5</span> — Plus de 75 avis Google
                </p>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
